import React, { createContext, useContext, useEffect, useState } from "react";
import api, { setAuthToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=auth
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response && e.response.status !== 401) {
        console.error("Auth check failed:", e);
      }
      setUser(false);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      // If arriving from Google OAuth callback, skip the /me check —
      // AuthCallback will process session_id and set the cookie first.
      if (window.location.hash?.includes("session_id=")) {
        if (mounted) setUser(false);
        return;
      }
      const data = await refresh();
      if (!mounted && data) setUser(data);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Cookie is now set by the server; keep token in memory as fallback
      // for third-party-cookie-blocked environments. Never persisted.
      setAuthToken(data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout call failed (proceeding anyway):", e);
    }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
